<script setup lang="ts">
import { computed } from 'vue'
import CheckpointStrategyList from './CheckpointStrategyList.vue'
import SlidePanel from '@/components/SlidePanel.vue'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import type { UdlGuideline } from './udl_index_types'

const PRINCIPLE_PANEL_CLASSES: Record<string, string> = {
  Representation: 'bg-[rgb(253,250,253)]',
  'Action & Expression': 'bg-[rgb(242,247,252)]',
  Engagement: 'bg-[rgb(247,255,245)]'
}

const props = withDefaults(
  defineProps<{
    guideline: UdlGuideline
    principle: string
    expanded?: boolean
    expandedCheckpoints?: Record<string, boolean>
  }>(),
  { expanded: true, expandedCheckpoints: () => ({}) }
)

const emit = defineEmits<{
  'update:expanded': [guidelineLabel: string, expanded: boolean]
  'update:checkpointExpanded': [checkpointRef: string, expanded: boolean]
}>()

const visibilityStore = useVisibilityStore()
const analytics = getAnalyticsTracker()

const panelId = computed(() => `udl-guideline-${props.guideline.label.replace(/\W+/g, '-').toLowerCase()}`)
const panelClass = computed(() => PRINCIPLE_PANEL_CLASSES[props.principle] || '')
const checkpointPrinciple = computed(() => (visibilityStore.editorVisibility ? props.principle : ''))

function toggleExpanded() {
  analytics.sendEvent(EVENT_NAMES.UDL_INDEX.TOGGLED_GUIDELINE, {
    udl_guideline: props.guideline.label,
    udl_principle: props.principle,
    udl_expanded: !props.expanded
  })
  emit('update:expanded', props.guideline.label, !props.expanded)
}

function isCheckpointExpanded(checkpointRef: string) {
  return props.expandedCheckpoints[checkpointRef] !== false
}
</script>

<template>
  <div>
    <h4 class="mt-2">
      <button
        type="button"
        class="text-left text-text-xl font-regular capitalize text-gray-700 hover:underline focus:outline-offset-4"
        :aria-expanded="expanded"
        :aria-controls="panelId"
        @click="toggleExpanded"
      >
        <span aria-hidden="true">{{ expanded ? '–' : '+' }}</span>
        {{ guideline.label }}
      </button>
    </h4>

    <SlidePanel :expanded="expanded">
      <div
        :id="panelId"
        data-testid="guideline-panel"
        :class="panelClass"
        class="rounded-xs border border-gray-400 p-4 text-text-md text-gray-900"
      >
        <ul class="list-none">
          <li v-for="checkpoint in guideline.checkpoints" :key="checkpoint.standard_long_ref">
            <CheckpointStrategyList
              :checkpoint="checkpoint"
              :principle="checkpointPrinciple"
              :expanded="isCheckpointExpanded(checkpoint.standard_long_ref)"
              @update:expanded="(checkpointRef, checkpointExpanded) => emit('update:checkpointExpanded', checkpointRef, checkpointExpanded)"
            />
          </li>
        </ul>
      </div>
    </SlidePanel>
  </div>
</template>
